import feathersStore from "../feathersStore";
import {pickLanguageWord} from '../utils/pickLanguageWord.js';
import { DateTime } from "luxon";

//Grouped product (a line whose product_recipes has isProduct entries) -> one row per part, prices
//scaled so the parts sum to the line. The paper Δελτίο prints these rows; a partsRule-1 line is
//also FILED as them (expandPartsForFiling). Per-part VAT/income category follow bundle-vat-v2 §4:
//the issue-time snapshot wins, then (rule 1 only) the part's packageVat on a package-priced line
//and the part's own isCommodity. With no stamp and no catalog, every part keeps the parent's
//product_vat and isCommodity - exactly what this expander returned before. Inline on purpose
//(same text in every app; the PDA suite lifts it by source), so no helper calls in here.
export const expandGroupedProduct = (item, allProducts = []) => {
  const subproducts = (item?.product_recipes || []).filter(r => r.isProduct);
  if (!subproducts?.length) return null;
  const sumBasePrice = subproducts.reduce((acc, r) =>
    acc + (r.unit === 'gr' ? r.price * r.quantity / 1000 : r.price * r.quantity), 0);
  if (sumBasePrice === 0) return null;
  const itemUnitTotal = item.product_free ? 0 : (item.product_totalPrice / item.product_quantity);
  const coefficient = itemUnitTotal / sumBasePrice;
  const snap = Array.isArray(item?.partsSnapshot) ? item.partsSnapshot : null;
  const rule1 = item?.partsRule === 1;
  return subproducts.map((r, i) => {
    const subProduct = (allProducts || []).find(p => p && r.productId && `${p._id}` === `${r.productId}`);
    const s = snap ? ((snap[i] && snap[i].productId === (r.productId || null)) ? snap[i] : snap.find(x => x && x.productId === (r.productId || null))) : null;
    const baseVat = subProduct?.vat !== undefined ? subProduct.vat : item.product_vat;
    const subVat = s ? s.vat : (rule1 && item?.product_packagePricing === true && +subProduct?.packageVat ? subProduct.packageVat : baseVat);
    const isCommodity = s ? s.isCommodity : (rule1 ? (subProduct?.isCommodity ?? item?.isCommodity) : item?.isCommodity);
    const isWeighted = r.unit === 'gr';
    const subTotalPrice = isWeighted
      ? +(r.price * coefficient * r.quantity / 1000 * item.product_quantity).toFixed(4)
      : +(r.price * coefficient * r.quantity * item.product_quantity).toFixed(4);
    return {
      groupedProductName: item.product_name,
      product_name: r.name,
      product_quantity: isWeighted ? item.product_quantity : r.quantity * item.product_quantity,
      product_totalPrice: subTotalPrice,
      product_vat: subVat,
      product_free: item.product_free ?? false,
      product_weightedQuantity: isWeighted ? r.quantity / 1000 : 0,
      productId: r.productId || null,
      isCommodity,
    };
  });
};

//----> BUNDLE PARTS RULE (bundle-vat-v2 §2-§6). Names and semantics are shared with every app.
//The ORDER-LINE marker product_packagePricing (written by the cart creators while the tenant switch
//is on) decides the rule; the ISSUE-TIME stamp partsRule/partsSnapshot goes on the ISSUED copies
//only (subOrder.subOrderItems here), never on order or cart lines.
const groupedRecipesOf = i => (i?.product_recipes || []).filter(r => r?.isProduct);

export const partsRuleOf = i => typeof i?.product_packagePricing === 'boolean' ? 1 : 0;

//One entry per isProduct recipe, in order: the VAT category and income flag this issuer files for
//that part. A part missing from the catalog read keeps the parent's product_vat and isCommodity.
const partsSnapshotOf = (i, rule, parts) => groupedRecipesOf(i).map(r => {
  const productId = r.productId || null;
  const part = (parts || []).find(p => p && productId && `${p._id}` === `${productId}`);
  const vat = rule === 1 && i.product_packagePricing === true && part && +part.packageVat
    ? part.packageVat
    : (part && part.vat !== undefined ? part.vat : i.product_vat);
  const isCommodity = rule === 1 ? (part?.isCommodity ?? i.isCommodity) : i.isCommodity;
  return { productId, vat, isCommodity };
});

export const stampItem = (i, parts) => {
  if (!i) return i;
  const rule = partsRuleOf(i);
  const copy = { ...i, partsRule: rule };
  if (groupedRecipesOf(i).length > 0) copy.partsSnapshot = partsSnapshotOf(i, rule, parts);
  else delete copy.partsSnapshot;
  return copy;
};

//COPIES - the inputs (order lines) are never touched.
export const stampPartsRule = (items, parts) => (items || []).map(i => stampItem(i, parts));

//The stored line an item is: by _clientItemKey first; by id only when the two do not both carry a key - a round stored
//from a till's cart carries cart ids that overlap order ids, so an id match alone can be another line (contract section 5).
const sameLine = (list, i) => {
  const items = list || [];
  const byKey = i?._clientItemKey ? items.find(x => x?._clientItemKey === i._clientItemKey) : null;
  return byKey || (i?.id != null ? items.find(x => x?.id === i.id && !(i?._clientItemKey && x?._clientItemKey)) : null) || null;
};

//A document filed for a stored TEMPORARY receipt follows that receipt; an unstamped (or absent)
//receipt makes every item legacy: partsRule 0, no snapshot.
export const stampLikeReceipt = (items, receipt) => {
  const aware = (receipt?.receiptItems || []).some(x => typeof x?.partsRule === 'number');
  return (items || []).map(i => {
    const r = aware && sameLine(receipt.receiptItems, i);
    const copy = { ...i, partsRule: r ? r.partsRule : 0 };
    if (r && Array.isArray(r.partsSnapshot)) copy.partsSnapshot = r.partsSnapshot; else delete copy.partsSnapshot;
    return copy;
  });
};

//A rewrite of subOrderItems that files nothing keeps the stamps the old items had.
export const carryStamps = (newItems, oldItems) => (newItems || []).map(i => {
  const o = sameLine(oldItems, i);
  if (!o || typeof o.partsRule !== 'number') return i;
  const copy = { ...i, partsRule: o.partsRule };
  if (Array.isArray(o.partsSnapshot)) copy.partsSnapshot = o.partsSnapshot;
  return copy;
});

//The ids the Δελτίο parts read must fetch: none unless a MARKED grouped line is present (only rule 1
//needs the catalog); then every grouped line's part ids, so the whole document is stamped from
//ONE read. Without a read, unmarked grouped lines are snapshotted at the parent's vat/isCommodity,
//which is what this app files for them (one whole line).
export const deltioPartIds = items => {
  const list = items || [];
  if (!list.some(i => partsRuleOf(i) === 1 && groupedRecipesOf(i).length > 0)) return [];
  return [...new Set(list.flatMap(i => groupedRecipesOf(i).map(r => r.productId).filter(Boolean)))];
};

//The ONE parts read of a web Δελτίο (readParts = feathersStore.findProductsForVatLookup, bounded).
//No read at all unless a marked grouped line is present. partsReadFailed = it threw or timed out:
//the caller must then file nothing at AADE (loadingOrderModal: the refused-Δελτίο path).
export const readDeltioParts = async (items, readParts) => {
  const ids = deltioPartIds(items);
  if (ids.length === 0) return { parts: [], partsReadFailed: false };
  try {
    const parts = await readParts(ids);
    return { parts: Array.isArray(parts) ? parts : [], partsReadFailed: false };
  } catch (error) {
    console.log('Δελτίο parts read failed - nothing is filed for this round', error);
    return { parts: [], partsReadFailed: true };
  }
};

//The stored Δελτίο lines: stamped COPIES. After a failed read they are stamped LEGACY (partsRule 0,
//no snapshot): what is stored is then exactly what this app stored before the rule (whole lines,
//today's buckets), and no document claims a rule it did not apply.
export const stampDeltioItems = (items, read) => read?.partsReadFailed
  ? stampLikeReceipt(items, null)
  : stampPartsRule(items, read?.parts || []);

//FILING rows of a partsRule-1 grouped line (null for every other line: it is filed whole, as
//today). Whole cents, so the myDATA lines and the vatN buckets agree to the cent: the parent line's
//cents are apportioned over the parts by largest remainder (each part within a cent of its exact
//share, and the parts sum to the parent line exactly), and each part's net comes from its OWN rate.
//Zero-value parts are dropped, as the builder drops zero-value lines. Same eligibility as the
//builder's line filter.
export const expandPartsForFiling = (item, parts = []) => {
  if (item?.partsRule !== 1 || item?.product_free || !(item?.product_netPrice > 0)) return null;
  const subs = expandGroupedProduct(item, parts);
  if (!subs) return null;
  const exact = subs.map(s => +s.product_totalPrice * 100);
  const cents = exact.map(x => Math.floor(x + 1e-6));
  let residual = Math.round(+item.product_totalPrice * 100) - cents.reduce((a, b) => a + b, 0);
  if (!Number.isFinite(residual)) return null; //unusable prices: file it whole, as today
  const byRemainder = exact.map((x, j) => ({ j, rem: x - cents[j] })).sort((a, b) => (b.rem - a.rem) || (a.j - b.j));
  while (residual > 0) {
    for (let n = 0; n < byRemainder.length && residual > 0; n++) { cents[byRemainder[n].j] += 1; residual -= 1; }
  }
  while (residual < 0) {
    let moved = false;
    for (let n = byRemainder.length - 1; n >= 0 && residual < 0; n--) {
      const j = byRemainder[n].j;
      if (cents[j] > 0) { cents[j] -= 1; residual += 1; moved = true; }
    }
    if (!moved) break;
  }
  return subs.map((s, j) => {
    const rate = +(feathersStore.settings?.vats?.find(v => +v.id === +s.product_vat)?.label) || 0;
    const net = Math.round(cents[j] / (1 + rate / 100)) / 100;
    const qty = +s.product_quantity;
    return { ...s, product_totalPrice: cents[j] / 100, product_netPrice: qty > 0 ? net / qty : 0 };
  }).filter((s, j) => cents[j] > 0);
};
//<---- BUNDLE PARTS RULE

export const issueDeltio = async (subOrder, response) => {
    try{   
  
      let printer = feathersStore.settings?.printers.find(prn => prn.invoicing);
      let itemsList = "";
      let itemsLines = [];
      let windowsLines = []; 
      const dateTime = DateTime.fromMillis(subOrder.createdAt).setLocale('el').toLocaleString(DateTime.DATETIME_FULL); 
      let vatAnalysis =""; 
  
      for (let item of subOrder.subOrderItems){
        let extraList = "";
        let ingredientList = "";
        let attributeList = "";
        const weightedString = item.product_weightedQuantity > 0 ? `${item.product_weightedQuantity * 1000} gr` : ""
        if(item?.features)for(let feature of item.features){extraList += feature.name + " "};
        if(item?.attributes)for(let attribute of item.attributes){attributeList += attribute.title + ": " + attribute.name + " "
          + (attribute?.quantity > 1 ? "x" + attribute.quantity + " " : "")};
        if(item?.ingredients)for(let ingredient of item.ingredients){ingredientList += ingredient.name};

        const expandedSubproducts = expandGroupedProduct(item);

        if(expandedSubproducts){
          // Grouped product: print each subproduct as its own line with "Part of:" subtitle
          for(const sub of expandedSubproducts){
            const subWeightedStr = sub.product_weightedQuantity > 0 ? `${sub.product_weightedQuantity * 1000} gr` : "";
            if(printer.nonEpos){
              const splittedSub = splitName(sub.product_name);
              itemsList = itemsList +
                '<align mode="left">' +
                `<text-line>${sub.product_quantity} X ${splittedSub[0]}${subWeightedStr ? ' ' + subWeightedStr : ''}</text-line>` +
                (splittedSub[1]?.length > 0 ? `<text-line>${splittedSub[1]}</text-line>` : "") +
                `<text-line>  ${pickLanguageWord("el", "partOf")}: ${sub.groupedProductName}</text-line>` +
                '</align>';
              pushWindowsLine(itemsLines, false, "left", `${sub.product_quantity} X ${sub.product_name}${subWeightedStr ? ' ' + subWeightedStr : ''}`, 8, false, false);
              pushWindowsLine(itemsLines, false, "left", `  ${pickLanguageWord("el", "partOf")}: ${sub.groupedProductName}`, 7, false, false);
            }else{
              itemsList = itemsList + '<text align = "left" />' +
                `<text em="true">${sub.product_quantity} X ${sub.product_name}${subWeightedStr ? ' ' + subWeightedStr : ''}&#10;</text>` +
                `<text lang="en" em="false" smooth="true">  ${pickLanguageWord("el", "partOf")}: ${sub.groupedProductName}&#10;</text>`;
            }
          }
          // Extras/ingredients printed after all subproduct lines (shared for the group)
          if(printer.nonEpos){
            if(extraList) itemsList += '<align mode="left">' + `<text-line>${extraList}</text-line>` + '</align>';
            if(attributeList) itemsList += '<align mode="left">' + `<text-line>${attributeList}</text-line>` + '</align>';
            if(ingredientList) itemsList += '<align mode="left">' + `<text-line>${pickLanguageWord("el", "without")}: ${ingredientList}</text-line>` + '</align>';
            const subHeader = (extraList ? extraList + " " : "") + (attributeList ? attributeList + " " : "") +
              (ingredientList ? pickLanguageWord("el", "without") + ": " + ingredientList + " " : "");
            if(subHeader) pushWindowsLine(itemsLines, false, "left", subHeader, 7, false);
          }else{
            if(extraList) itemsList += `<text lang="en" em="false" smooth="true">${extraList}&#10;</text>`;
            if(attributeList) itemsList += `<text lang="en" em="false" smooth="true">${attributeList}&#10;</text>`;
            if(ingredientList) itemsList += `<text lang="en" em="false" smooth="true">${pickLanguageWord("el", "without")}: ${ingredientList}&#10;</text>`;
          }
        }else{
          // Normal product: existing behavior
          if(printer.nonEpos){
            const splittedName = splitName(item.product_name);
            itemsList = itemsList +
            '<align mode="left">' +
            `<text-line>${item.product_quantity} X ${splittedName[0]}` +
            `<x-position>340</x-position><text>${weightedString}</text></text-line>` +
            (splittedName[1]?.length > 0 ? `<text-line>${splittedName[1]}</text-line>` : "") +
            (item?.product_size?.name ? `<text-line>${item?.product_size?.name}</text-line>` : "") +
              (extraList ? `<text-line>${extraList}</text-line>` : "") +
              (attributeList ? `<text-line>${attributeList}</text-line>` : "") +
              (ingredientList ? `<text-line>${pickLanguageWord("el", "without")}: ${ingredientList}</text-line>` : "") +
            '</align>';

            const subHeader = (extraList ? extraList + " "  : "")
              + (attributeList ? attributeList + " "  : "") +
              (ingredientList ? pickLanguageWord("el", "without") + ": " + ingredientList + " "  : "");

            pushWindowsLine(itemsLines, false, "left", `${item.product_quantity} X ${splittedName[0]}` , 8, false, false, weightedString?.length > 0);
            itemsLines.push({text: `${weightedString}`, xPosition: mmToPt(50), continued: false});
            if(splittedName[1]?.length > 0)pushWindowsLine(itemsLines, false, "left", `${splittedName[1]}` , 8, false, false);
            if(item?.product_size?.name)pushWindowsLine(itemsLines, false, "left", `${item.product_size.name}` , 8, false, false);
            if(subHeader)pushWindowsLine(itemsLines, false, "left", subHeader, 7, false);

          }else{
            itemsList = itemsList +  '<text align = "left" />' +
              `<text em="true">${item.product_quantity} X ${item.product_name} ${item?.product_size?.name ?? ""} ${weightedString}&#10;</text>` +
              '<text align = "left" />' +
                (extraList ? `<text lang="en" em="false" smooth="true">${extraList}&#10;</text>` : "") +
                (attributeList ? `<text lang="en" em="false" smooth="true">${attributeList}&#10;</text>` : "") +
                (ingredientList ? `<text lang="en" em="false" smooth="true">${pickLanguageWord("el", "without")}: ${ingredientList}&#10;</text>` : "");
          }
        }
      }
  
      let vatAnalysisHeader = 
        '<align mode="left">' +    
        '<text-line>Ανάλυση ΦΠΑ</text-line>' + 
        `<text-line>Συντελεστής` + 
        `<x-position>${'245'}</x-position><text>ΦΠΑ%</text>` +
        `<x-position>${'350'}</x-position><text>ΦΠΑ</text>` +
        `<x-position>${'455'}</x-position><text>Κ. Αξία</text-line>` +
        '</align>';  
      
      for(let vat of feathersStore.settings.vats){
        if(subOrder[`vat${vat.id}`]?.vatAmount > 0){
          vatAnalysis = vatAnalysis +
            '<align mode="left">' +
              `<text-line>Κατ. ΦΠΑ: ${vat.id}` + 
              `<x-position>${'245'}</x-position><text>${getVat(vat.id)}</text>` +
              `<x-position>${'350'}</x-position><text>${parse_fix(subOrder[`vat${vat.id}`].vatAmount)}</text>` +
              `<x-position>${'455'}</x-position><text>${parse_fix(subOrder[`vat${vat.id}`].underlyingValue)}</text-line>` +
            '</align>';            
        }      
      }

      let req =   
        '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">' +
          '<s:Body>' +
            '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">' +
              '<text align = "center" />' +
              constructCompanyTitle() +
              '<feed unit="24"/>' + 
              `<text lang="en" em="true">ΔΕΛΤΙΟ ΠΑΡΑΓΓΕΛΙΑΣ&#10;</text>` +
              `<text em="true">${pickLanguageWord("el", "table")}: ${subOrder.tableName}&#10;</text>` +
              '<feed unit="24"/>' + 
              '<text align = "left" />' +
              `<text em="false" smooth="true">${dateTime}&#10;</text>` +
              `<text em="false" smooth="true">ΑΡΙΘΜΟΣ ΔΕΛΤΙΟΥ: ${subOrder.numericId}&#10;</text>` +
              '<text align = "center" />' +
              `<text >------------------------------------------&#10;</text>` +          
                itemsList + 
              '<feed unit="24"/>' +         
              '<text align = "center" />' +
              `<text>------------------------------------------&#10;</text>` +
              '<text align = "left" />' +         
              '<feed unit="24"/>' +
              `<text width="1" height="1" em="false"  smooth="true">${response?.footer}&#10;</text>` +
              '<feed unit="24"/>' +
              `<symbol align = "center" type="qrcode_model_2" level="level_q">${response?.qrcode}</symbol>` +        
              '<feed unit="24"/>' +
              `<text align = "center">------------------------------------------&#10;</text>` +
              `<text>${feathersStore.user.username}&#10;</text>` +           
              '<feed unit="24"/>' +            
              '<cut/>' +
            '</epos-print>' +
          '</s:Body>' +
        '</s:Envelope>';
      
      let payload = {ip: printer.ip, devid: printer.devId, req};
  
      if(printer.nonEpos){

        let branch = null;
        if(feathersStore.settings?.BRANCHES_ENABLED){
          branch = feathersStore?.selectedBranch ?? null;
        }
         
        req =  
          '<?xml version="1.0" encoding="UTF-8"?>' +
          '<document>' +
            '<set-cp/>' +
            '<align mode="center">' +
              constructCompanyTitleNonEpos() +             
            '<line-feed />' +
            '<bold>' +             
              `<text-line>ΔΕΛΤΙΟ ΠΑΡΑΓΓΕΛΙΑΣ</text-line>`  +                        
              `<text-line>${pickLanguageWord("el", "table")}: ${subOrder.tableName}</text-line>` +
            '</bold>' +
            '</align>' +
            '<line-feed />' +
            '<align mode="left">' +
            `<text-line>${dateTime}</text-line>` +
            `<text-line>ΑΡΙΘΜΟΣ ΔΕΛΤΙΟΥ: ${subOrder.numericId}</text-line>` +
            '</align>' +
            '<align mode="center">' +
            `<text-line>------------------------------------------</text-line>` +          
                itemsList + 
            '<line-feed />' +         
            '<align mode="center">' +
            `<text-line>------------------------------------------</text-line>` +
            '</align>' +
            '<align mode="left">' +   
            '<line-feed/>' +  
            vatAnalysisHeader +
            vatAnalysis +          
            '<line-feed/>' +         
            '<line-feed/>' +    
            `<text-line>${response?.footer}</text-line>` +
            '<line-feed/>' +
            '</align>' +     
            '<align mode="center">' +   
              `<qrcode ecl="M" size="4">${response?.qrcode}</qrcode>` +        
            '<line-feed />' +  
            `<text-line>Πάροχος myData: app.simplycloud.gr</text-line>` +
            '</align>' +     
            '<align mode="center">' +       
            `<text-line>------------------------------------------</text-line>` +            
            `<text-line></text-line>` +
            `<text-line>BRINGFOOD</text-line>` +
            '</align>' +
            '<line-feed />' +                     
            '<paper-cut />' +
          '</document>' ;
         
        Object.assign(payload, {nonEpos: true, req});

        constructCompanyTitleWindows(windowsLines, feathersStore.settings);
        if(branch)constructCompanyTitleWindowsForBranch(windowsLines, feathersStore.settings, branch);
        pushWindowsLine(windowsLines, false, "center", `${dateTime}`, 9, true);
        pushWindowsLine(windowsLines, false, "center", 'ΔΕΛΤΙΟ ΠΑΡΑΓΓΕΛΙΑΣ', 10, false, true);
        pushWindowsLine(windowsLines, false, "left", `ΑΡΙΘΜΟΣ ΔΕΛΤΙΟΥ: ${subOrder.numericId}`, 10, true, false);     
        pushWindowsLine(windowsLines, true, "center", 
          `${pickLanguageWord("el", "table")}: ${subOrder.tableName}` , 10, false, false);
        for(const line of itemsLines){
          windowsLines.push(line)
        }

         windowsLines.push({insertStraightLine: true})   
        pushWindowsLine(windowsLines, false, "left", `${'Ανάλυση ΦΠΑ'}` , 8, false, false, false);  
        pushWindowsLine(windowsLines, false, "left", `${'Συντελεστής'}` , 8, false, false, true);         
        windowsLines.push({text: `${'ΦΠΑ%'}`, xPosition: mmToPt(28), continued: true});
        windowsLines.push({text: `${'ΦΠΑ'}`, xPosition: mmToPt(42), continued: true});
        windowsLines.push({text: `${'Κ. Αξία'}`, xPosition: mmToPt(56), continued: false}); 
        windowsLines.push({insertStraightLine: true})
      
        for (let vat of feathersStore.settings.vats){
          if(subOrder[`vat${vat.id}`]?.vatAmount > 0){          
            pushWindowsLine(windowsLines, false, "left", `Κατ. ΦΠΑ: ${vat.id}` , 8, false, false, true);         
            windowsLines.push({text: `${getVat(vat.id)}`, xPosition: mmToPt(28), continued: true});
            windowsLines.push({text: `${parse_fix(subOrder[`vat${vat.id}`].vatAmount)}`, xPosition: mmToPt(42), continued: true});
            windowsLines.push({text: `${parse_fix(subOrder[`vat${vat.id}`].underlyingValue)}`, xPosition: mmToPt(56), continued: false}); 
          }
        }  

        windowsLines.push({feed: 1});      
        pushWindowsLine(windowsLines, false, "left", `${response?.footer}` , 8, false, false, false, true);         
        windowsLines.push({feed: 2});
        windowsLines.push({qrcode: response?.qrcode});
        windowsLines.push({feed: 1});

        pushWindowsLine(windowsLines, false, "left", 
          `ΤΟ ΠΑΡΟΝ ΕΙΝΑΙ ΠΛΗΡΟΦΟΡΙΑΚΟ ΣΤΟΙΧΕΙΟ ΚΑΙ ΔΕΝ ΑΠΟΤΕΛΕΙ ΝΟΜΙΜΗ ΦΟΡΟΛΟΓΙΚΗ ΑΠΟΔΕΙΞΗ/ΤΙΜΟΛΟΓΙΟ. THE PRESENT DOCUMENT IS ISSUED ONLY FOR INFORMATION PURPOSES AND DOES NOT STAND FOR A VALID TAX RECEIPT/INVOICE.`
          , 8, false, false, false, true);       

        if(printer?.windows || printer?.raster){
          Object.assign(payload, {
            windows: !!printer?.windows,
            raster: !!printer?.raster,
            req: "",
            lang: false,
            windowsLines          
          })
        }
        
      }      
      await feathersStore.createLocalPrint(payload);   
      return payload;
    }catch(err){
  //    this.sendingOrder = false;
      console.log('Could not create printJob!', err);
    };  
  } 

  const getVat = id => {
    return feathersStore.settings.vats.find(v => +v.id === +id)?.label.toString().padStart(2, '0') + "%"
  }
  
  //One myDATA line per PART of a partsRule-1 grouped line: the part's own VAT category and income
  //category, the parent's notes, and the parent's name in AdditionalNotes. Every field the whole
  //line sends is sent, from the same formulas.
  const deltioPartLine = (item, part, index) => ({
    "LineNumber": +index + 1,
    "Barcode": "",
    "Name": `${part.product_name}`.substring(0, 30),
    "Quantity": +part.product_quantity,
    "Notes": [
      ...(item?.features || []).map(f => `${f.name} `),
      ...(item?.attributes || []).map(a => `${a.title}: ${a.name} `),
      ...(item?.ingredients || []).map(i => `XΩΡΙΣ: ${i.name} `)
    ].toString().substring(0, 70),
    "AdditionalNotes": `${item.product_name}`.substring(0, 70),
    "Total": +parse_fix(part.product_netPrice * part.product_quantity),
    "VATAmount": +parse_fix(part.product_totalPrice - (part.product_netPrice * part.product_quantity)),
    "VATPerc": feathersStore.settings.vats.find(v => +v.id === +part.product_vat)?.label,
    "TotalWithVAT": +parse_fix(part.product_totalPrice),
    "UnitPrice": +parse_fix(part.product_netPrice),
    "UnitDesc": 0,
    "IncomeCode": part?.isCommodity ? "category1_1" : "category1_2",
    "EInvoiceIncomeClassTypeCode": "E3_561_003",
    "ExpenseCode": "",
    "EInvoiceExpenseClassTypeCode": null,
    "ReasonForTaxExp": "15",
    "VatCategory": +part.product_vat,
    "MeasurementUnit": "1",
    "withheldAmount": 0.0,
    "withheldPercentCategory": null,
    "stampDutyAmount": 0.00,
    "stampDutyPercentCategory": null,
    "feesAmount": 0.0,
    "feesPercentCategory": null,
    "otherTaxesAmount": 0.0,
    "otherTaxesPercentCategory": null,
  });

  //parts: the Δελτίο parts read (readDeltioParts). A stamped line expands from its snapshot, so
  //parts only matter for a rule-1 line stamped without one.
  export const constructMyDataForDeltio = async (subOrder, file, line, numericId, parts = []) => {
  
    const date = new Date();
    const day = date.getDate().toString().padStart(2, "0"); 
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    const isoDate = year + "-" + month + "-" + day;       
    const isoTime = hours + ":" + minutes + ":" + seconds;
  
    let details = [];
    let vatAnalysis = [];
  
    subOrder.subOrderItems
    .filter(itm => !itm?.product_free && itm.product_netPrice > 0)
    //a partsRule-1 grouped line is filed as its parts (bundle-vat-v2 §4); every other line as today
    .flatMap(itm => (expandPartsForFiling(itm, parts) || [null]).map(part => ({item: itm, part})))
    .forEach(({item, part}, index) => {     
      if(part){ details.push(deltioPartLine(item, part, index)); return; }
  
      let detailObject =
      {
        "LineNumber": +index + 1,
        "Barcode": "",
        "Name": `${item.product_name} ${item?.product_size?.name ?? ""}`.substring(0, 30), 
        "Quantity": +item.product_quantity,
        "Notes": [
          ...item?.features.map(f => `${f.name} `) ?? [],
          ...item?.attributes.map(a => `${a.title}: ${a.name} `) ?? [],
          ...item?.ingredients.map(i => `XΩΡΙΣ: ${i.name} `) ?? [],
          ...((item?.product_recipes || []).filter(r => r.isProduct).length > 0
            ? [(item.product_recipes.filter(r => r.isProduct).map(r => r.name).join(' · '))]
            : [])
        ].toString().substring(0, 70),
        "AdditionalNotes": "", 
        "Total": +parse_fix(item.product_netPrice * item.product_quantity),       
        "VATAmount": +parse_fix(item.product_totalPrice - (item.product_netPrice * item.product_quantity)),
        "VATPerc": feathersStore.settings.vats.find(v => +v.id === +item.product_vat)?.label,
        "TotalWithVAT": +parse_fix(item.product_totalPrice),
        "UnitPrice": +parse_fix(item.product_netPrice),
        "UnitDesc": 0,
        "IncomeCode": item?.isCommodity ? "category1_1" : "category1_2",
        "EInvoiceIncomeClassTypeCode": "E3_561_003",
        "ExpenseCode": "",
        "EInvoiceExpenseClassTypeCode": null,
        "ReasonForTaxExp": "15",
        "VatCategory": +item.product_vat,
        "MeasurementUnit": "1",
        "withheldAmount": 0.0,
        "withheldPercentCategory": null,
        "stampDutyAmount": 0.00,
        "stampDutyPercentCategory": null,
        "feesAmount": 0.0,
        "feesPercentCategory": null,
        "otherTaxesAmount": 0.0,
        "otherTaxesPercentCategory": null,     
      }      
  
      details.push(detailObject);     
  
    })  
  
    for(let vat of feathersStore.settings.vats){
      const label = feathersStore.settings.vats.find(v => +v.id === +vat.id)?.label
      if(subOrder[`vat${vat.id}`]?.vatAmount > 0){
        vatAnalysis.push({
          "Name": "VAT" + label + ".00%",
          "Percentage": +label,
          "VatAmount": +subOrder[`vat${vat.id}`].vatAmount,
          "UnderlyingValue": +subOrder[`vat${vat.id}`].underlyingValue
        })
      }      
    } 
  
    const totalNetAmount = calculateTotalNetAmount(subOrder);

    const myDataSuffix = feathersStore.settings?.myDataSuffix ?? "";
    const myDataBranch = feathersStore.settings?.myDataBranch ?? "";
  
    const body = 
      {
        "ActionTypeId": 1,
        "InvoiceDate": isoDate,
        "InvoiceTypeCode": "8.6",
        "InvoiceTypeName": "ΔΕΛΤΙΟ ΠΑΡΑΓΓΕΛΙΑΣ",
        "InvoiceTypeSeries": `${numericId}`,
        "InvoiceTypeSeriesName": "Custom",
        "InvoiceTypeNumber": `ΔΠ${myDataSuffix ? "-" + myDataSuffix : ""}`,
        "InvoiceTypeNumberName": `ΔΠ-${myDataSuffix ? myDataSuffix : "Α"}`,
        "Currency": "EURO",
        "CurrencyCode": "EUR",
        "IsRetailInvoice": true,
        "IsDelayedInvoice": false,
        "transmissionFailure": "",
        "DelayedDocumentID": "",
        "tableAA": `${subOrder.tableName}`,
        "selfPricing": false,
        "SubmissionToMyDATA": true,
        "vatPaymentSuspension": false,
        "CorrelatedInvoices": "",
        "distributionInfo": null,
        "dispatchDate": isoDate,
        "dispatchTime": isoTime,
        "vehicleNumber": null,
        "WebOrderNumber": null,
        "StoreComments": "",
        "CustomerComments": "",
        "movePurpose": 1,
        "ShippingMethod": "COURIER",
        "Issuer": {
          "CompanyName": `${feathersStore.settings.companyName}`,
          "CompanyLegalName": `${feathersStore.settings?.companyLegalName ?? feathersStore.settings.companyName}`,
          "CompanyActivity":  `${feathersStore.settings.occupation}`,
          "CompanyAddress": {
            "Country": "GR",
            "CountryCode": "GR",
       //     "Region":  `${this.settings.taxAddress?.region}`,
            "City":  `${feathersStore.settings.taxAddress?.city}`,
            "Municipality":  `${feathersStore.settings.taxAddress?.municipality}`,
            "Street":  `${feathersStore.settings.taxAddress?.street}`,
            "Number":  `${feathersStore.settings.taxAddress?.number}`,
            "Postal":  `${feathersStore.settings.taxAddress?.postal}`
          },
          "CompanyPhones": [
            `${feathersStore.settings.phoneNumbers[0]?.phoneNumber}`
          ],
          "CompanyEmails": [
            `${feathersStore.settings.email}`
          ],
          "CompanyCurrency": "€",
          "CompanyVatNumber": `${feathersStore.settings.afm}`,       
          "CompanyTaxAuthority": `ΔΟΥ ${feathersStore.settings.doy}`,
          "CompanyBranch": myDataBranch ? `${myDataBranch}` : "0",
          "CompanyBranchCode": "",
          "CompanyPosId": ""         
        },       
        "Payment": 
      //    "TotalPieces": persistedReceipt.totalPieces,
          [
            {
              "PaymentMethodType": "ΜΕΤΡΗΤΟΙΣ",
              "PaymentMethodTypeCode": 3,
              "PaymentAmount": +parse_fix(subOrder.subOrderTotal),
              "PaymentNotes": "ΜΕΤΡΗΤΟΙΣ"
            }
          ]
        ,    
        "lstLineItem": details,    
        "Summary": {
          "totalNetValue": +parse_fix(totalNetAmount),
          "totalWithheldAmount": 0.00,
          "totalFeesAmount": 0.00,
          "totalStampDutyAmount": 0.00,  
          "TotalVATAmount": +parse_fix(subOrder.subOrderTotal - totalNetAmount),
          "totalOtherTaxesAmount": 0.00,
          "totalDeductionsAmount": 0.00,
          "totalGrossValue": +parse_fix(subOrder.subOrderTotal),
          "totalAllowances": 0.00,
          "totalAllowancesWithoutLines": 0.00,
          "totalCharges": 0.00,
          "totalSpecialCharges": 0.00      
     //     "TotalPayableAmount": persistedReceipt.receiptTotal
        },    
        "VatAnalysis": vatAnalysis,
        "AllowancesCharges": [
          {
            "Type": "",
            "Description": "",
            "Code": "",
            "Amount": 0.00,
            "VatPercentage": 0.00,
            "VatAmount": 0.00
          }
        ],       
        "SendMethod": "0",
        "Email": [
            "support@simplypos.com"
        ]
      }    
          
      try{
        const response = await feathersStore.postToMyData(body); 
        if(response?.data){
          return {       
            qrcode:  `${response.data.invoiceURL}`,
            footer: `${response.data.invoiceMark} ${response.data.invoiceUid} ${response.data.authenticationCode}`,
            mark:  `${response.data.invoiceMark}`
          } 
        }else{
          console.log(response);
          await logError(response, subOrder, file, line);
          return;
        }
      }catch(error){
        console.log(error);
        await logError(error, subOrder, file, line);
        return;
      }   
  }
  
  const constructCompanyTitle = () => {
    return(
      `<text dw="true" dh="true" />`+
      `<text lang="en" em="true">${feathersStore.settings.companyName}&#10;</text>` +
      '<feed unit="24"/>' + 
      `<text dw="false" dh="false" />`+
      `<text lang="en" em="true">${feathersStore.settings?.companyLegalName ?? feathersStore.settings.companyName}&#10;</text>` +
      '<feed unit="24"/>' + 
      `<text lang="en" em="true">${feathersStore.settings.address}&#10;</text>` + 
      `<text lang="en" em="true">${pickLanguageWord("el", "afm")}: ${feathersStore.settings.afm} ${pickLanguageWord("el", "doy")} ${feathersStore.settings.doy}&#10;</text>` +
      `<text lang="en" em="true">${feathersStore.settings.occupation}&#10;</text>` 
    )
  }
  
  const constructCompanyTitleNonEpos = () => {
    const phonesArray = feathersStore.settings?.phoneNumbers?.filter(number => number?.numberEnabled);
    return(
      `<bold><text-line size="1:1">${feathersStore.settings.companyName}</text-line></bold>` +
      '<line-feed />' +
      `<bold><text-line>${feathersStore.settings?.companyLegalName ?? feathersStore.settings.companyName}</text-line></bold>` +
      '<line-feed />' +
      '<bold>' +
        `<text-line>${feathersStore.settings.address}</text-line>` + 
        `<text-line>${pickLanguageWord("el", "afm")}: ${feathersStore.settings.afm} ${pickLanguageWord("el", "doy")} ${feathersStore.settings.doy}</text-line>` +
        `<text-line>${pickLanguageWord("el", 'phone')}: ${phonesArray?.length > 0 ? phonesArray[0]?.phoneNumber : ""}</text-line>` +
        `<text-line>${feathersStore.settings.occupation}</text-line>` +
      '</bold>'
    )
  }  
  
  const calculateTotalNetAmount = persistedReceipt => {
    let returnVal = 0;
    for(let vat of feathersStore.settings.vats){
      returnVal += +persistedReceipt[`vat${vat.id}`].underlyingValue
    }
    return returnVal;
  }
  
  export const logError = async(error, persistedReceipt, file, line) => {
    const payload = {
      errorKind: "my_data",
      origin: "Web app",
      file: file,
      line: line,
      error,
      persistedReceipt
    }
    await feathersStore.createLogEntry(payload)
  }
  
  export const splitName = (name) => {
    let returnArray = [];
    returnArray[0] = name.substring(0, 24);
    returnArray[1] = name.substring(24);
    return returnArray;
  }
  
  export const parse_fix = price => {
    return price ? parseFloat(price).toFixed(2) : 0;
  }  
  
  export const calculateVats = items => {
    const givenVats = feathersStore.settings?.vats;   
    let vatsObj = {}
    for(let vat of givenVats){
      let vatAmount = items.filter(item => +item.product_vat === +vat.id)
        .map(i => i?.product_free ? 0 : (i.product_totalPrice - (i.product_netPrice * i.product_quantity)))        
        .reduce((a,b) => (+a + +b), 0).toFixed(2);
      let underlyingValue = items.filter(item => +item.product_vat === +vat.id)
        .map(i => i?.product_free ? 0 : (i.product_quantity * i.product_netPrice))        
        .reduce((a,b) => (+a + +b), 0).toFixed(2);
    Object.assign(vatsObj, {[`vat${vat.id}`]: {vatAmount, underlyingValue}})
    }  
    return vatsObj;
  }

  //The vatN buckets of a document whose partsRule-1 grouped lines are filed as their parts: the same
  //rows constructMyDataForDeltio files, so the buckets equal its lines summed by category. With no
  //rule-1 line this IS calculateVats(items).
  export const calculateVatsExpanded = (items, parts = []) =>
    calculateVats((items || []).flatMap(i => expandPartsForFiling(i, parts) || [i]));

  export const mmToPt = (mm) => {
  return  Math.round(mm * 2.83464567);
}

export const pushWindowsLine = (windowsLines = [], bold = false, align = "left", text = "", fontSize = 14
  , dottedLine = false, black = false, continued = false, hasWidth = false) => {
  windowsLines.push({ bold, align, text, fontSize, dottedLine, black, continued, hasWidth })
}

export const constructCompanyTitleWindows = (windowsLines = [], settings) => {
  const phonesArray = settings?.phoneNumbers?.filter(number => number?.numberEnabled);
  pushWindowsLine(windowsLines, true, "center", `${settings.companyName}`, 14, false); 
  pushWindowsLine(windowsLines, false, "center", `${settings?.companyLegalName ?? settings.companyName}`, 9, false);
  pushWindowsLine(windowsLines, false, "center", `${settings.address}`, 9, false);
  pushWindowsLine(windowsLines, false, "center", `${pickLanguageWord("el", "afm")}: ${settings.afm} ${pickLanguageWord("el", "doy")} ${settings.doy}`, 9, false);
  pushWindowsLine(windowsLines, false, "center", `${pickLanguageWord("el", 'phone')}: ${phonesArray?.length > 0 ? phonesArray[0]?.phoneNumber : ""}`, 9, false);
  pushWindowsLine(windowsLines, false, "center", settings.occupation, 9, true);  
}

export const constructCompanyTitleWindowsForBranch = (windowsLines = [], settings, sectionBranch) => {
  const branchObj = settings?.branches.find(br => br.id === sectionBranch);
  pushWindowsLine(windowsLines, false, "center", `${pickLanguageWord("el", 'branch')} ${sectionBranch}: ${branchObj?.name} ${branchObj?.phone}`, 9, false);
  pushWindowsLine(windowsLines, false, "center", `${branchObj.address}`, 9, false); 
} 